import { TextFieldController } from '@/components/controls';
import { ConfirmationDialog } from '@/components/dialogs';
import { useOptions } from '@/hooks';
import { telematicsBoxForm } from '@/utils/config';
import {
  Person as PersonIcon,
  RemoveCircle as RemoveCircleIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Button,
  Card,
  CardActionArea,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { Fragment, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';

export function TelematicsBoxForm({
  defaultValues,
  canEdit,
  onSubmit,
  onDelete,
  onRemove,
}) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [removeVehicle, setRemoveVehicle] = useState(null);
  const {
    query: { data: telematicsBoxTypes },
  } = useOptions('telematicsBoxType');
  const {
    control,
    formState: { dirtyFields, isSubmitting },
    handleSubmit,
    reset,
    watch,
  } = useForm({ defaultValues });
  const isDirty = Object.keys(dirtyFields).length > 0;

  function handleCancel() {
    reset();
  }

  function toggleDeleteDialog() {
    setDeleteOpen((prev) => !prev);
  }

  const handleRemoveClick = (person) => (event) => {
    event.preventDefault();

    setRemoveVehicle(person);
  };

  function handleRemove() {
    onRemove(removeVehicle._id);
    setRemoveVehicle(null);
  }

  function isDisabled(fieldName) {
    return !(
      canEdit &&
      (telematicsBoxForm[fieldName]?.editable || !defaultValues?._id)
    );
  }

  return (
    <Fragment>
      <form id="radio-form" onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={1}>
          <Stack
            spacing={1}
            direction="row"
            sx={{ justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Typography variant="h6" sx={{ p: 1 }}>
              {defaultValues?._id ? defaultValues.imei : 'New Telematics Box'}
            </Typography>
            <Chip
              variant="outlined"
              icon={
                <Chip variant="outlined" label="Last Poll Time" size="small" />
              }
              label={
                defaultValues?.lastPoll?.time
                  ? format(
                      new Date(defaultValues.lastPoll.time),
                      'dd/MM/yyyy HH:mm:ss',
                    )
                  : 'Never'
              }
            />
          </Stack>
          <Stack spacing={1} direction="row" sx={{ flexWrap: 'wrap' }}>
            <TextFieldController
              name="imei"
              label="IMEI"
              control={control}
              disabled={isDisabled('imei')}
              rules={{ required: 'Required', maxLength: 20 }}
            />
            <TextFieldController
              name="imsi"
              label="IMSI"
              control={control}
              disabled={isDisabled('imsi')}
            />
            <TextFieldController
              name="type"
              label="Type"
              options={telematicsBoxTypes}
              control={control}
              disabled={isDisabled('type')}
            />
            <TextFieldController
              name="label"
              label="Label"
              control={control}
              disabled={isDisabled('label')}
              rules={{ maxLength: 50 }}
            />
          </Stack>
          <Divider>
            <Typography variant="caption" color="textSecondary">
              Assigned Vehicles
            </Typography>
          </Divider>
          {defaultValues?.vehicles?.length > 0 ? (
            defaultValues.vehicles.map((vehicle) => (
              <Card
                key={vehicle.identificationNumber}
                variant="outlined"
                sx={{ width: 320, mr: 1, mb: 1 }}
              >
                <CardActionArea
                  component={Link}
                  to={`/resources/vehicles/${encodeURIComponent(vehicle.identificationNumber)}`}
                >
                  <CardHeader
                    avatar={
                      <Avatar src={vehicle.picture}>
                        <PersonIcon />
                      </Avatar>
                    }
                    action={
                      <Tooltip title="Remove">
                        <IconButton
                          onClick={handleRemoveClick(vehicle)}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <RemoveCircleIcon />
                        </IconButton>
                      </Tooltip>
                    }
                    title={vehicle.registrationNumber}
                    subheader={vehicle.fleetNumber}
                  />
                </CardActionArea>
              </Card>
            ))
          ) : (
            <Typography variant="body2" color="textSecondary">
              There is no vehicle assigned to this box.
            </Typography>
          )}
          <Stack spacing={1} direction="row">
            <Button
              color="primary"
              type="submit"
              disabled={!isDirty || isSubmitting}
            >
              Save
            </Button>
            <Button
              color="primary"
              disabled={!isDirty || isSubmitting}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              color="error"
              onClick={toggleDeleteDialog}
              // eslint-disable-next-line react-hooks/incompatible-library
              disabled={!watch('_id')}
            >
              Delete
            </Button>
            <Button component={Link} to="test" disabled={!defaultValues?._id}>
              Test & Configure
            </Button>
          </Stack>
        </Stack>
      </form>
      <ConfirmationDialog
        action="Remove"
        open={!!removeVehicle}
        itemId={
          removeVehicle
            ? [removeVehicle.forenames, removeVehicle.surname].join(' ') ||
              removeVehicle.collarNumber ||
              removeVehicle.code
            : ''
        }
        onOk={handleRemove}
        onCancel={() => setRemoveVehicle(null)}
      />
      <ConfirmationDialog
        action="Delete"
        open={deleteOpen}
        itemId={defaultValues?.imei}
        onOk={onDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </Fragment>
  );
}
